import React, { useState, useEffect, useRef } from 'react';
import { useAppStore } from '../../store/appStore';
import Avatar from '../common/Avatar';

// Live list of users who currently have this camera's page open (i.e. are in a
// position to control it). Backed by a presence WebSocket that the backend
// broadcasts to whenever someone joins or leaves.
export default function CameraViewers({ cameraId, avatarSize = 20, fontSize = 11 }) {
  const { token, user } = useAppStore();
  const [viewers, setViewers] = useState([]);
  // userId of whoever most recently sent a PTZ control input — highlighted in orange so
  // everyone can see who's driving the camera. Server clears it after ~25s of no input.
  const [controllerId, setControllerId] = useState(null);
  const wsRef = useRef(null);
  const retryRef = useRef(null);
  const pingRef = useRef(null);

  useEffect(() => {
    let closed = false;

    function connect() {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${window.location.host}/api/presence/${cameraId}?token=${token}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'viewers') {
            setViewers(data.viewers);
            setControllerId(data.controllerId ?? null);
          }
        } catch (_) {}
      };
      ws.onopen = () => {
        // App-level keepalive in addition to the server's protocol ping.
        pingRef.current = setInterval(() => {
          if (ws.readyState === 1) ws.send('ping');
        }, 25000);
      };
      ws.onclose = () => {
        if (pingRef.current) clearInterval(pingRef.current);
        if (!closed) retryRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => { try { ws.close(); } catch (_) {} };
    }

    connect();
    return () => {
      closed = true;
      if (retryRef.current) clearTimeout(retryRef.current);
      if (pingRef.current) clearInterval(pingRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch (_) {} }
    };
  }, [cameraId, token]);

  if (viewers.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
      <svg width={fontSize + 3} height={fontSize + 3} viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" style={{ flexShrink: 0 }}>
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
        {viewers.map(v => {
          const isMe = user && v.userId === user.id;
          const isController = v.userId === controllerId;
          // Orange "controlling" highlight wins over the cyan "you" tint.
          const border = isController ? 'var(--accent-orange)' : (isMe ? 'rgba(0,212,255,0.4)' : 'var(--border-dim)');
          const bg = isController ? 'rgba(244,123,32,0.14)' : (isMe ? 'rgba(0,212,255,0.1)' : 'var(--bg-surface)');
          const textColor = isController ? 'var(--accent-orange)' : (isMe ? 'var(--accent-cyan)' : 'var(--text-secondary)');
          return (
            <div
              key={v.userId}
              title={`${v.username}${isMe ? ' (you)' : ''}${isController ? ' · Active Controller' : ''} · ${v.role?.replace(/_/g, ' ') || ''}${v.connections > 1 ? ` · ${v.connections} tabs` : ''}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '2px 8px 2px 3px',
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: '999px',
                boxShadow: isController ? '0 0 8px rgba(244,123,32,0.45)' : 'none',
                transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
              }}
            >
              <Avatar src={v.avatar} name={v.username} userId={v.userId} size={avatarSize} ring={isController ? 'var(--accent-orange)' : undefined} ringWidth={3} />
              <span style={{
                fontFamily: 'var(--font-display)', fontSize: `${fontSize}px`, fontWeight: 600,
                color: textColor,
                whiteSpace: 'nowrap', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {isMe ? 'You' : v.username}
              </span>
              {isController && (
                <span className="animate-pulse" title="Active Controller" style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent-orange)', boxShadow: '0 0 5px var(--accent-orange)', flexShrink: 0 }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
